import type { Side } from 'driver.js';

/**
 * =============================================================================
 * HOME TOUR CONTENT — edit this file to change copy, order, or targets
 * =============================================================================
 * After internal testing, update text / button labels / step order here only.
 */

/** Button labels shown in the welcome modal and tour popovers. */
export const HOME_TOUR_BUTTONS = {
  next: 'Next',
  previous: 'Back',
  skip: 'Skip tour',
  finish: 'Finish',
  restart: 'Take a tour',
  start: 'Start tour',
};

/** Floating replay-tour control (bottom-right stack). */
export const HOME_TOUR_FAB = {
  tooltip: 'Take a tour',
  ariaLabel: 'Take a tour of Home',
};

/** Welcome modal (shown before the spotlight tour starts). */
export const HOME_TOUR_WELCOME = {
  title: 'Welcome to Home',
  student:
    'Home brings together everything you need in one place, helping you continue learning, stay on top of important updates, and quickly access your most-used areas.',
  educator:
    'Home gives you one place to manage your teaching, stay up to date, and quickly return to the work that matters most.',
};

export type HomeTourStepId =
  | 'aula-logo'
  | 'role-toggle'
  | 'spaces'
  | 'space-progress'
  | 'space-engagement'
  | 'space-cta'
  | 'recent-updates'
  | 'messages'
  | 'upcoming-assignments'
  | 'recently-viewed'
  | 'starred'
  | 'quick-links'
  | 'manage';

/**
 * Step order per role — reorder these arrays to change the tour sequence.
 * Both roles: Recent Updates comes after space steps, before Messages.
 */
export const HOME_TOUR_STUDENT_ORDER: HomeTourStepId[] = [
  'aula-logo',
  'role-toggle',
  'spaces',
  'space-progress',
  'space-cta',
  'recent-updates',
  'messages',
  'upcoming-assignments',
  'recently-viewed',
  'starred',
  'quick-links',
  'manage',
];

export const HOME_TOUR_EDUCATOR_ORDER: HomeTourStepId[] = [
  'aula-logo',
  'role-toggle',
  'spaces',
  'space-engagement',
  'space-cta',
  'recent-updates',
  'messages',
  'upcoming-assignments',
  'recently-viewed',
  'starred',
  'quick-links',
  'manage',
];

type HomeTourStepContent = {
  /** Spotlight title (shared). Use titleStudent / titleEducator when they differ. */
  title?: string;
  titleStudent?: string;
  titleEducator?: string;
  /** Shared description. Use descriptionStudent / descriptionEducator when they differ. */
  description?: string;
  descriptionStudent?: string;
  descriptionEducator?: string;
  /** CSS selector for the highlighted element */
  element: string;
  /** Popover side relative to the highlight */
  side?: Side;
  /** Popover align along that side */
  align?: 'start' | 'center' | 'end';
};

/**
 * All step titles, descriptions, selectors, and popover placement.
 * Change any string here — order is controlled by the arrays above.
 */
export const HOME_TOUR_STEPS: Record<HomeTourStepId, HomeTourStepContent> = {
  'aula-logo': {
    title: 'Aula Logo',
    description: 'Select the Aula logo at any time to quickly return to Home.',
    element: '[data-tour="home-aula-logo"]',
    side: 'bottom',
  },

  'role-toggle': {
    title: 'Switch Role',
    descriptionStudent:
      'If you have both learner and educator roles, use this toggle to switch between your learner and educator Home.',
    descriptionEducator:
      'If you also have a learner role, use this toggle to switch between your educator and learner Home.',
    element: '#home-switchrole-widget',
    side: 'bottom',
  },

  spaces: {
    title: 'Your Spaces',
    descriptionStudent:
      'Your learning, Course Community and training spaces are all in one place. Select a space name to open its Space Navigation page.',
    descriptionEducator:
      'View all your teaching, Course Community and training spaces in one place. Select a space name to open its navigation page.',
    element: '#home-space-widget',
    side: 'bottom',
  },

  'space-progress': {
    title: 'Track Your Progress',
    description:
      'See your learning progress for each space. New Materials may be added throughout your course, so check back regularly to stay up to date.',
    element: '[data-tour="home-space-card-progress"]',
    side: 'bottom',
  },

  'space-engagement': {
    title: 'Engagement Analytics',
    description:
      'See how students are engaging with the content in each space.',
    element: '[data-tour="home-space-card-engagement"]',
    side: 'bottom',
  },

  'space-cta': {
    titleStudent: 'Start Learning / Continue Learning',
    titleEducator: 'Open Material/Edit Material',
    descriptionStudent:
      'Start learning for the first time or continue from the last Material page you accessed, without searching through the space.',
    descriptionEducator:
      'Open Materials for the first time or Edit Materials from the last Material page you accessed, without searching through the space.',
    element: '[data-tour="home-space-card-cta"]',
    side: 'bottom',
  },

  'recent-updates': {
    title: 'Recent Updates',
    descriptionStudent:
      'Stay up to date with the latest Announcements and Common room activity. React and comment directly from here, or filter to view educator posts only. Post colors indicate whether an update was created by an educator or a student.',
    descriptionEducator:
      'Stay up to date with activity across Announcements and Common room. React and comment directly from Home.',
    element: '#home-recent-updates-widget',
    side: 'left',
    align: 'start',
  },

  messages: {
    title: 'Messages',
    description:
      'Catch up on your latest conversations and unread messages. Select any conversation to view it and reply without leaving Home.',
    element: '#home-messages-widget',
    side: 'right',
    align: 'start',
  },

  'upcoming-assignments': {
    title: 'Upcoming Assignments',
    descriptionStudent:
      'View your upcoming assignments in due date order, with assignments due soon shown first. Select any assignment to view its details or submit your work.',
    descriptionEducator:
      'View upcoming assignments ordered by due date and quickly access assignment details and submissions.',
    element: '#home-upcoming-assignments-widget',
    side: 'right',
    align: 'start',
  },

  'recently-viewed': {
    title: 'Recently Viewed Spaces',
    description:
      'Quickly return to spaces you have recently visited. Selecting a space opens the last area you accessed within it, such as Materials, Announcements or Common room.',
    element: '#home-recently-viewed-spaces-widget',
    side: 'left',
    align: 'start',
  },

  starred: {
    title: 'Starred Spaces',
    descriptionStudent:
      'Keep your favorite spaces within easy reach. You can star or un-star spaces whenever you like.',
    descriptionEducator:
      'Keep your most frequently used spaces within easy reach. You can star or un-star spaces whenever you like.',
    element: '#home-starred-spaces-widget',
    side: 'right',
    align: 'start',
  },

  'quick-links': {
    title: 'Quick Links',
    descriptionStudent:
      'Access useful institutional services and resources directly from Home.',
    descriptionEducator:
      'Access important institutional tools and resources directly from Home.',
    element: '#home-quick-links-widget',
    side: 'right',
    align: 'start',
  },

  manage: {
    title: 'Personalize Your Home',
    description:
      'Hide or show widgets to organize Home in a way that works best for you.',
    element: '#home-manage-widget',
    side: 'left',
    align: 'start',
  },
};
